// Centralized strings object for i18n preparation.
// Organized by namespace to mirror react-i18next structure.
// When adopting react-i18next, strings.auth.welcome becomes t('auth:welcome').
// No restructuring needed — only the access syntax changes.
//
// Shape, variable name and export deliberately match the catalogue on
// `origin/echo-33-ex`, which holds every other namespace. That branch and this
// one both create this file, so a merge is otherwise a conflict on the whole
// file rather than on one key. Only `onboarding` belongs here; when the two
// meet, the resolution is to keep both sets of keys.
//
// The `t` helper lives in ./t.js rather than here, so this file stays a pure
// catalogue and merges without touching logic.

const strings = {
  // Onboarding redesign (ECH-107)
  onboarding: {
    session: {
      expired:
        "Your session expired. Sign in to continue. Your answers are saved.",
      signIn: "Sign in to continue",
    },
    progress: {
      label: "Onboarding progress",
      count: "{current} of {total}",
    },
    layout: {
      back: "Back",
      logoAlt: "Elevate Aviation",
      skipToContent: "Skip to content",
      contentLabel: "Onboarding step",
      opensInNewTab: "(opens in a new tab)",
      documentTitle: "{step} — Elevate Aviation",
    },
    error: {
      heading: "Something went wrong on this step",
      body: "Your answers are saved. Try again, or go back and continue from the previous step.",
      retry: "Try again",
    },
    // Step names, used in the page title and read out on each step change.
    steps: {
      welcome: "Welcome",
      dob: "Date of birth",
      create_account: "Create your account",
      verify_email: "Verify your email",
      goals: "Your goals",
      person_flow: "About you",
      profile: "Your profile",
      demographics: "About you (optional)",
      done: "All set",
    },
    welcome: {
      headline: "A New Era of Aviation Career Development",
      tagline: "Built For Aviation Professionals, By Aviation Professionals",
      subtitle: "Connecting people and industry with the tools needed to soar.",
      joiningAs: "I'm joining as",
      person: {
        label: "I'm a Person",
        description:
          "Grow your career, find a mentor, give back, or build new skills in aviation.",
        cta: "Get started",
      },
      organization: {
        label: "We're an Organization",
        description:
          "Build mentorship programs, develop talent, and connect with aviation professionals.",
        // Stated plainly rather than left to fail on click.
        unavailable: "Coming soon",
        // One line at card width, deliberately. The footer band is bottom
        // anchored so both cards end level; a hint that wraps to three lines
        // while the other card's call to action is one makes the two bands
        // start at visibly different heights.
        unavailableHint: "Not open for signup yet.",
      },
      haveAccount: "Already have an account?",
      signIn: "Sign In",
    },
    dob: {
      heading: "What's your date of birth?",
      body: "Echo is a 16+ platform. Please enter your date of birth to continue.",
      legend: "Date of birth",
      day: "Day",
      month: "Month",
      year: "Year",
      dayPlaceholder: "DD",
      monthPlaceholder: "MM",
      yearPlaceholder: "YYYY",
      invalid: "Please enter a valid date of birth.",
      incomplete: "Enter your complete date of birth to continue.",
      privacy:
        "Your date of birth is used only for age verification and is kept private. We do not share this information with other users.",
      continue: "Continue",
    },
    ageGate: {
      // Warm, not a rejection: this is a child being redirected, not an error.
      heading: "It looks like you're a bit too young to join Echo.",
      subheading:
        "Echo is designed for users aged 16 and older. We're excited for you to be part of our community in the future!",
      body: "Once you reach the minimum age requirement, we'd love to welcome you aboard. In the meantime, keep exploring your passion for aviation — we'll be here when you're ready!",
      programsTitle: "Elevate Aviation — Youth Programs",
      close: "Close",
      goBack: "Go back",
      programs: {
        aceOnTour: {
          name: "ACE on Tour",
          description: "Our Annual ACE on Tour events for youth aged 12–18.",
          ages: "Ages 12–18",
        },
        careerExploration: {
          name: "Aviation Career Exploration",
          description: "Aviation Career Exploration for youth aged 11–18.",
          ages: "Ages 11–18",
        },
        juniorJets: {
          name: "Junior Jets",
          description: "Junior Jets for youth aged 5–10.",
          ages: "Ages 5–10",
        },
        aviationBirthdayParties: {
          name: "Aviation Birthday Parties",
          description: "Aviation Birthday Parties for youth aged 8 and up.",
          ages: "Ages 8+",
        },
      },
    },
    createAccount: {
      heading: "Create your account",
      body: "You'll use this to sign in and to receive your verification code.",
      email: "Email address",
      emailInvalid: "Enter a valid email address.",
      password: "Password",
      passwordShowAriaLabel: "display the password",
      passwordHideAriaLabel: "hide the password",
      confirm: "Confirm password",
      confirmMismatch: "Passwords do not match.",
      rules: {
        length: "At least 8 characters",
        uppercase: "One uppercase letter",
        number: "One number",
        symbol: "One symbol",
      },
      policiesHeading: "Read and accept to continue",
      submit: "Create account",
      incomplete:
        "Enter a valid email, meet every password requirement, confirm your password, and accept all three policies.",
      failed: "We could not create your account. Please try again.",
      duplicateEmail: "This email is already registered.",
      signIn: "Sign in",
      signInSuffix: " to resume onboarding or access your account.",
    },
    policies: {
      consentPrefix: "I have read and accept the ",
      terms: { title: "Terms of Service" },
      privacy: { title: "Privacy Policy" },
      community: { title: "Community Guidelines" },
      scrollPrompt: "Scroll to the end to accept",
      accept: "Accept",
      cancel: "Cancel",
      short: { title: "Short Policy" },
      documentLabel: "Policy document",
      close: "Close",
      loading: "Loading policies...",
      loadFailed:
        "The current policies could not be loaded. Please try again before creating an account.",
      retry: "Retry",
      changed:
        "The policies were updated. Please review the current documents and accept them again.",
    },
    verifyEmail: {
      heading: "Check your email",
      body: "We sent a 4-digit code to {email}. Enter it below to confirm your address.",
      digitLabel: "Digit {position}",
      submit: "Verify",
      incomplete: "Enter the complete 4-digit verification code.",
      resend: "Send the code again",
      resent: "We've sent a new code.",
      resendFailed: "We could not send a new code. Please try again.",
      failed: "That code is not valid.",
    },
    profile: {
      heading: "Complete your profile",
      body: "Start with the required details below. This is how others on Echo will discover you.",
      requiredHeading: "Required profile details",
      mainPhoto: "Profile photo",
      mainPhotoHint:
        "Choose a photo with your face clearly visible. The mobile app compares your profile photo during identity verification; uploading it here does not verify your identity.",
      mainPhotoRequired: "Profile photo is required.",
      additionalPhotos: "Additional photos",
      additionalPhotosHint:
        "Optional, up to 3. Add more photos that feel like you.",
      upload: "Choose a photo or drag and drop",
      replace: "Replace photo",
      remove: "Remove {name}",
      invalidPhoto: "Choose a JPG, PNG, GIF, or WebP image.",
      photoTooLarge: "That photo is over the 10MB limit. Choose a smaller one.",
      uploadFailed: "We could not upload that photo. Please try again.",
      uploadPending: "Wait for all photo uploads to finish.",
      incomplete:
        "Add a profile photo, first name, last name, city, and province or state.",
      uploading: "Uploading photo...",
      firstName: "First name",
      lastName: "Last name",
      firstNameRequired: "First name is required.",
      lastNameRequired: "Last name is required.",
      role: "Current role",
      company: "Organization",
      employmentHeading: "Employment history (optional)",
      employmentIncomplete:
        "Complete or remove any employment entries you added.",
      currentJob: "Current job",
      currentJobHint:
        "Add your current role and organization together as one current job.",
      currentRoleRequired: "Add a current role or clear the organization.",
      pastRole: "Past role",
      pastRoleTitle: "Role",
      pastCompany: "Organization",
      addPastRole: "Add past role",
      removePastRole: "Remove past role",
      address: "Location",
      city: "City",
      province: "Province or state",
      postalCode: "Postal code",
      country: "Country",
      cityRequired: "City is required.",
      provinceRequired: "Province or state is required.",
      biography: "Biography",
      languages: "Languages spoken",
      optional: "Optional",
      optionalHeading: "Enhance your profile (optional)",
      optionalBody:
        "You can skip the remaining details and add them later from your profile.",
      skipOptional: "Skip optional details",
      continue: "Continue",
    },
    demographics: {
      heading: "A few optional questions",
      optional: "Optional",
      body: "Demographic information helps Echo measure its impact and support underrepresented groups in aviation.",
      why: "Why we ask",
      whyHeading: "Why Echo asks",
      whyBody:
        "These optional answers help us understand who our programs reach and improve equity in aviation. Your answers stay private unless you choose to show a field on your profile, and choosing not to answer will never limit your experience.",
      close: "Close",
      visibleOnProfile: "Visible on profile",
      hiddenFromProfile: "Not shown to users",
      selfDescribeRequired:
        "Describe your identity or deselect Another identity.",
      selfDescribe: "How would you describe your gender identity?",
      selfDescribePlaceholder: "Share only what feels right",
      gender: "Gender identity",
      ethnicity: "Ethnicity",
      lgbtq: "2SLGBTQ+ identification",
      disability: "Disability status",
      skip: "Skip and submit",
      save: "Save and submit",
    },
    done: {
      heading: "You're all set, welcome aboard",
      body: "Echo is personalized to your goals. Here's where to start:",
      person: "Your individual Echo experience is ready.",
      organization: "Your organization workspace is ready.",
      enter: "Enter Echo",
      failed: "We could not finish setting up your account. Please try again.",
      // Shown on the step the member is returned to, not on Done.
      rejected:
        "Something in your details could not be saved. Please check them and try again.",
      saving: "Setting up your account",
      savingBody: "This takes a moment.",
      retry: "Try again",
      nextSteps: {
        learn: {
          title: "Explore the Learn library",
          cta: "Open Learn",
        },
      },
      app: {
        heading: "Get the Echo app",
        bodyPhone:
          "Connect with mentors and aviation professionals in the Echo app.",
        bodyScan:
          "Connect with mentors and aviation professionals in the Echo app. Scan a code with your phone to download it.",
        googlePlay: "Get it on Google Play (opens in a new tab)",
        appStore: "Download on the App Store (opens in a new tab)",
        qrGooglePlay: "QR code for the Echo app on Google Play",
        qrAppStore: "QR code for the Echo app on the App Store",
      },
      goals: {
        receive_mentorship: "Find a mentor",
        provide_mentorship: "Mentor others",
        networking: "Grow your network",
        grow_career: "Advance your career",
        build_skills: "Build new skills",
      },
    },
    goals: {
      heading: "What brings you to Echo?",
      body: "Select all that apply \u2014 we'll personalize your experience around your goals.",
      legend: "Select your goals",
      options: {
        grow_career: {
          label: "Grow my career",
          description:
            "Advance to the next level with structured guidance and learning opportunities.",
        },
        networking: {
          label: "Expand my network",
          description:
            "Connect with professionals across aviation and build lasting relationships.",
        },
        provide_mentorship: {
          label: "Mentor others",
          description:
            "Share your experience and make a meaningful impact on someone's journey.",
        },
        receive_mentorship: {
          label: "Be mentored",
          description:
            "Get personalized guidance from experienced professionals in your desired field.",
        },
        build_skills: {
          label: "Build new skills",
          description:
            "Develop your skillset through tailored learning pathways.",
        },
      },
      continueNone: "Select at least one goal",
      continueOne: "Continue with 1 goal",
      continueMany: "Continue with {count} goals",
    },
    // ECH-116. Headings mirror `flowMeta` in the Figma export.
    // Every taxonomy id resolves here, and the id's own `vocabulary.member`
    // prefix IS the path — so `t(`onboarding.vocab.${id}`)` works for a
    // sector, a sub-type or a stage without the caller knowing which it holds.
    vocab: {
      aviation_sectors: {
        aerospace_and_manufacturing: "Aerospace & Manufacturing",
        air_traffic_services: "Air Traffic Services",
        aircraft_maintenance: "Aircraft Maintenance",
        aircraft_manufacturing: "Aircraft Manufacturing",
        airworthiness_and_quality_assurance:
          "Airworthiness & Quality Assurance",
        airport_ground_operations: "Airport Ground Operations",
        airport_operations: "Airport Operations",
        aviation_consulting: "Aviation Consulting",
        aviation_management_and_leadership: "Aviation Management & Leadership",
        aviation_non_profit_leadership: "Aviation Non-Profits",
        aviation_safety: "Aviation Safety",
        aviation_sales_and_insurance: "Aviation Sales & Insurance",
        aviation_security_and_screening: "Aviation Security & Screening",
        business_aviation: "Business Aviation",
        cargo_operations: "Cargo Operations",
        customer_service: "Customer Service",
        data_analytics: "Data Analytics",
        drone_rpas_operations: "Drone / RPAS Operations",
        emerging_technologies: "Emerging Technologies",
        engineering: "Engineering",
        flight_attendant_cabin_crew: "Flight Attendant / Cabin Crew",
        flight_dispatch: "Flight Dispatch",
        flight_operations: "Flight Operations",
        human_resources: "Human Resources",
        information_technology: "Information Technology",
        pilot_aeroplane: "Pilot (Aeroplane)",
        pilot_helicopter_rotary_wing: "Pilot (Helicopter / Rotary-Wing)",
        professional_skills: "Professional Skills",
        rcaf: "RCAF",
        regulatory_and_government_aviation: "Regulatory & Government Aviation",
        training_and_instruction: "Training & Instruction",
      },
      // One shared vocabulary: "any", "not_sure_yet" and "military_pilot" recur
      // across sectors, so 52 entries cover 135 option slots.
      aviation_subtypes: {
        adaptability: "Adaptability",
        aerial_work_pilot: "Aerial Work Pilot",
        aerospace_control: "Aerospace Control",
        aerospace_engineering: "Aerospace Engineering",
        air_operations: "Air Operations",
        air_traffic_control: "Air Traffic Control",
        air_traffic_services: "Air Traffic Services",
        aircraft_maintenance: "Aircraft Maintenance",
        airline_pilot: "Airline Pilot",
        airport_operations_analytics: "Airport Operations Analytics",
        any: "Any",
        avionics: "Avionics",
        business_intelligence_and_reporting:
          "Business Intelligence & Reporting",
        charter_pilot: "Charter Pilot",
        communication: "Communication",
        conflict_resolution: "Conflict Resolution",
        corporate_business_aviation_pilot:
          "Corporate / Business Aviation Pilot",
        decision_making: "Decision Making",
        emotional_intelligence: "Emotional Intelligence",
        ems_medevac: "EMS / Medevac",
        firefighting: "Firefighting",
        flight_data_and_performance: "Flight Data & Performance",
        flight_information_services: "Flight Information Services",
        flight_instructor: "Flight Instructor",
        flight_service_specialist_fss: "Flight Service Specialist (FSS)",
        government_pilot: "Government Pilot",
        intelligence_and_surveillance: "Intelligence & Surveillance",
        job_applications_interviews: "Job Applications / Interviews",
        leadership_and_officer_careers: "Leadership & Officer Careers",
        leadership_management: "Leadership / Management",
        maintenance: "Maintenance",
        maintenance_analytics: "Maintenance Analytics",
        medevac_pilot: "Medevac Pilot",
        military_pilot: "Military Pilot",
        networking: "Networking",
        newly_licensed_pilot: "Newly Licensed Pilot",
        not_sure_yet: "Not Sure Yet",
        offshore_operations: "Offshore Operations",
        other: "Other",
        problem_solving: "Problem Solving",
        public_speaking: "Public Speaking",
        quality_assurance: "Quality Assurance",
        safety_and_risk_analysis: "Safety & Risk Analysis",
        structures: "Structures",
        student_pilot: "Student Pilot",
        teamwork: "Teamwork",
        test_pilot: "Test Pilot",
        time_management: "Time Management",
        tourism_and_charter: "Tourism & Charter",
        utility_and_resource_operations: "Utility & Resource Operations",
        weather_and_forecasting: "Weather & Forecasting",
        workplace_confidence: "Workplace Confidence",
      },
      mentor_stages: {
        early_career: "Early Career",
        mid_career_professional: "Mid-Career Professional",
        senior_professional: "Senior Professional",
        executive_leadership: "Executive / Leadership",
      },
      connection_types: {
        students_and_new_entrants: "Students & New Entrants",
        industry_professionals: "Industry Professionals",
        instructors_and_trainers: "Instructors & Trainers",
        managers_and_leaders: "Managers & Leaders",
        executives: "Executives",
        recruiters_and_hiring_managers: "Recruiters & Hiring Managers",
        entrepreneurs_and_founders: "Entrepreneurs & Founders",
      },
      community_roles: {
        student_new_entrant: "Student / New Entrant",
        industry_professional: "Industry Professional",
        instructor_trainer: "Instructor / Trainer",
        manager_leader: "Manager / Leader",
        executive: "Executive",
        recruiter_hiring_manager: "Recruiter / Hiring Manager",
        entrepreneur_founder: "Entrepreneur / Founder",
      },
      career_goals: {
        explore_aviation_careers: "Explore Aviation Careers",
        earn_new_licenses_or_ratings: "Earn New Licenses or Ratings",
        develop_leadership_skills: "Develop Leadership Skills",
        transition_to_a_new_aviation_role: "Transition to a New Aviation Role",
        advance_in_my_current_role: "Advance In My Current Role",
        become_an_industry_leader: "Become an Industry Leader",
        find_employment_opportunities: "Find Employment Opportunities",
        grow_my_industry_knowledge: "Grow My Industry Knowledge",
        start_or_grow_an_aviation_business:
          "Start or Grow an Aviation Business",
      },
      learning_formats: {
        interactive_courses: "Interactive Courses",
        webinars: "Webinars",
        educational_videos: "Educational Videos",
        articles_and_blogs: "Articles & Blogs",
        presentations: "Presentations",
      },
    },
    sectors: {
      searchLabel: "Search sectors",
      searchPlaceholder: "Search sectors...",
      selected: "{count} selected",
      clearAll: "Clear all",
      noMatches: 'No sectors match "{search}"',
      manySelected:
        "You can refine these later from your profile — picking a lot now makes onboarding longer.",
      // Nested to mirror the sector id, so the lookup needs no string surgery.
      questions: {
        aviation_sectors: {
          pilot_aeroplane: {
            seeking: "What type of aeroplane pilots are you interested in?",
            offering:
              "What aeroplane pilot pathways do you have experience in?",
          },
          pilot_helicopter_rotary_wing: {
            seeking: "What type of helicopter pilots are you interested in?",
            offering:
              "What helicopter pilot pathways do you have experience in?",
          },
          air_traffic_services: {
            seeking: "What area of Air Traffic Services interests you?",
            offering:
              "What area of Air Traffic Services do you have experience in?",
          },
          aircraft_maintenance: {
            seeking: "What area of maintenance interests you?",
            offering:
              "What area of aircraft maintenance do you have experience in?",
          },
          data_analytics: {
            seeking: "What area of data analytics interests you?",
            offering: "What area of data analytics do you have experience in?",
          },
          professional_skills: {
            seeking: "Which professional skills interest you?",
            offering: "Which professional skills do you have experience in?",
          },
          rcaf: {
            seeking: "What area of the RCAF interests you?",
            offering: "What area of the RCAF do you have experience in?",
          },
        },
      },
    },
    subflow: {
      incomplete: "Answer all required questions to continue.",
      dotsLabel: "Goal {current} of {total}",
      continue: "Continue",
      selectedCount: "{count}/{max}",

      // Questions more than one sub-flow can ask, so the copy cannot differ
      // depending on which goal happened to reach it first.
      questions: {
        communityRole: {
          label:
            "How would you describe your role within the aviation community?",
          hint: "Select up to 3",
        },
        connectionTypes: {
          label: "What types of connections are you seeking?",
        },
        experienceStage: {
          label: "What career stage best describes your experience?",
        },
        careerGoals: { label: "What are your career goals?" },
        learningFormats: { label: "How do you prefer to learn?" },
      },
      receive_mentorship: {
        heading: "Finding your mentor",
        subheading: "Tell us what kind of guidance you're looking for.",
        sectorsLabel: "What areas do you want mentorship in?",
        sectorsSubtitle:
          "Select as many sectors as you like. We'll help you find the right guides.",
        stageLabel: "What career stage would you like to learn from?",
        stageOptional: "(optional — select all that apply)",
      },
      provide_mentorship: {
        heading: "Share your expertise",
        subheading: "Help us direct you to people you can genuinely help.",
        sectorsLabel: "What areas do you have experience in?",
        sectorsSubtitle:
          "Share your experience — we'll connect you with people you can genuinely help.",
      },
      networking: {
        heading: "Building your network",
        subheading: "Tell us who you'd like to connect with.",
        sectorsLabel:
          "What areas of aviation would you like to establish connections in?",
      },
      grow_career: {
        heading: "Career trajectory",
        subheading: "Tell us where you are and where you're headed.",
        sectorsLabel: "What areas of aviation are you looking to grow in?",
      },
      build_skills: {
        heading: "Skills roadmap",
        subheading: "Tell us what you'd like to learn.",
        sectorsLabel: "Which areas of aviation interest you?",
        sectorsSubtitle:
          "We'll recommend courses and resources matched to your interests.",
      },
      skills: {
        cardHeading: "Your learning path is ready to personalize",
        basisNamed:
          "Based on your interest in {sectors}, Echo will recommend courses, webinars and resources tailored to your aviation areas. Just tell us how you like to learn.",
        basisGeneric:
          "Based on the interests you shared, Echo will recommend courses, webinars and resources tailored to your aviation areas. Just tell us how you like to learn.",
        andMore: "{named} and {count} more",
        and: " and ",
      },
    },
    placeholder: {
      heading: "{step}",
      body: "This screen has not been built yet. It is registered in the step sequence so routing, progress and the guards can be exercised before the screen exists.",
      ticket: "Built by {ticket}",
      next: "Continue",
    },
  },
  // Public account deletion page, linked from the Google Play store listing.
  // Google requires it to name the app, give the steps to request deletion and
  // say what is deleted and what is kept, so keep all three when editing.
  deleteAccount: {
    documentTitle: "Delete your account — Echo by Elevate Aviation",
    logoAlt: "Elevate Aviation",
    heading: "Delete your Echo by Elevate Aviation account",
    intro:
      "You can ask Elevate Aviation to delete your Echo account and the data associated with it at any time.",
    request: {
      heading: "How to request deletion",
      lead: "Send an email to",
      leadEnd: "and include:",
      fullName: "Your full name",
      accountEmail: "The email address on your Echo account",
      statement: "A note that you want your account and data deleted",
      confirmation:
        "We will reply by email to confirm once the deletion is complete.",
    },
    deleted: {
      heading: "What is deleted",
      account: "Your account and sign in details",
      profile:
        "Your profile, including your name, photos, biography, location, work and education history and goals",
      demographics: "Any demographic information you chose to share",
      messages: "Your messages and the files you sent in chat",
    },
    kept: {
      heading: "What is kept",
      transactions:
        "Records of purchases and transactions, which we keep for as long as tax and accounting law requires",
    },
  },
};

export default strings;
